import React, { useState, useRef } from 'react';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { supabase } from './supabaseClient';

export default function LoginPage() {
  const [email, setEmail]         = useState('');
  const [password, setPassword]   = useState('');
  const [error, setError]         = useState('');
  const [loading, setLoading]     = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const captchaRef = useRef(null);

  async function handleLogin(e) {
    e.preventDefault();
    setError('');

    if (!captchaToken) {
      setError('Please complete the captcha verification.');
      return;
    }

    setLoading(true);

    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
      options: { captchaToken },
    });

    if (error) {
      setError('Incorrect email or password. Please try again.');
      setLoading(false);
      captchaRef.current?.resetCaptcha();
      setCaptchaToken(null);
    }
  }

  return (
    <div style={S.page}>
      {/* Left Panel */}
      <div style={S.left}>
        <div style={S.leftContent}>
          <div style={S.brandRow}>
            <span style={S.brandIcon}>📊</span>
            <span style={S.brandName}>Excelsior Financial Dashboard</span>
          </div>
          <h1 style={S.tagline}>
            Financial Intelligence<br />at Your Fingertips
          </h1>
          <p style={S.taglineSub}>
            Multi-branch financial tracking, real-time insights,
            and complete business visibility — all in one place.
          </p>
          <div style={S.statsRow}>
            {[
              { icon: '🏪', label: 'Multi-Branch' },
              { icon: '📈', label: 'Real-Time' },
              { icon: '🔐', label: 'Secure' },
            ].map(s => (
              <div key={s.label} style={S.statBadge}>
                <span>{s.icon}</span>
                <span style={S.statLabel}>{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel */}
      <div style={S.right}>
        <div style={S.card}>
          <h2 style={S.cardTitle}>Welcome Back</h2>
          <p style={S.cardSub}>Sign in to your dashboard</p>

          <form onSubmit={handleLogin}>
            <div style={S.group}>
              <label style={S.label}>Email Address</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                style={S.input}
                placeholder="you@example.com"
              />
            </div>

            <div style={S.group}>
              <label style={S.label}>Password</label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                style={S.input}
                placeholder="••••••••"
              />
            </div>

            {/* hCaptcha Widget */}
            <div style={S.captchaWrapper}>
              <HCaptcha
                sitekey={process.env.REACT_APP_HCAPTCHA_SITE_KEY}
                onVerify={token => setCaptchaToken(token)}
                onExpire={() => setCaptchaToken(null)}
                ref={captchaRef}
              />
            </div>

            {error && <div style={S.error}>⚠️ {error}</div>}

            <button
              type="submit"
              style={{
                ...S.btn,
                opacity: (!captchaToken || loading) ? 0.6 : 1,
                cursor: (!captchaToken || loading) ? 'not-allowed' : 'pointer',
              }}
              disabled={!captchaToken || loading}
            >
              {loading ? '⏳ Signing in…' : 'Sign In →'}
            </button>
          </form>

          <p style={S.footer}>
            Excelsior Financial Dashboard · Secured by Supabase
          </p>
        </div>
      </div>
    </div>
  );
}

const S = {
  page: {
    display: 'flex',
    minHeight: '100vh',
    fontFamily: "'Segoe UI', Arial, sans-serif",
  },
  left: {
    flex: 1,
    background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #1e3a5f 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 48px',
  },
  leftContent: { maxWidth: 420 },
  brandRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 40,
  },
  brandIcon: { fontSize: 36 },
  brandName: {
    fontSize: 20,
    fontWeight: 700,
    color: '#ffffff',
    letterSpacing: '-0.5px',
  },
  tagline: {
    fontSize: 38,
    fontWeight: 800,
    color: '#ffffff',
    lineHeight: 1.2,
    margin: '0 0 20px',
    letterSpacing: '-0.5px',
  },
  taglineSub: {
    fontSize: 15,
    color: '#94a3b8',
    lineHeight: 1.7,
    margin: '0 0 40px',
  },
  statsRow: { display: 'flex', gap: 12 },
  statBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 20,
    padding: '8px 16px',
  },
  statLabel: { color: '#cbd5e1', fontSize: 13, fontWeight: 600 },
  right: {
    width: '480px',
    background: '#f8fafc',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 32px',
  },
  card: { width: '100%', maxWidth: 400 },
  cardTitle: {
    fontSize: 28,
    fontWeight: 800,
    color: '#0f172a',
    margin: '0 0 8px',
    letterSpacing: '-0.5px',
  },
  cardSub: {
    fontSize: 14,
    color: '#64748b',
    margin: '0 0 28px',
  },
  group: { marginBottom: 18 },
  label: {
    display: 'block',
    fontSize: 13,
    fontWeight: 600,
    color: '#374151',
    marginBottom: 8,
  },
  input: {
    width: '100%',
    border: '1.5px solid #e2e8f0',
    borderRadius: 10,
    padding: '12px 14px',
    fontSize: 14,
    outline: 'none',
    background: '#ffffff',
    boxSizing: 'border-box',
    color: '#0f172a',
  },
  captchaWrapper: {
    margin: '16px 0',
  },
  error: {
    background: '#fef2f2',
    border: '1px solid #fecaca',
    color: '#dc2626',
    borderRadius: 10,
    padding: '12px 14px',
    fontSize: 13,
    marginBottom: 16,
  },
  btn: {
    width: '100%',
    background: 'linear-gradient(135deg, #1e1b4b 0%, #4f46e5 100%)',
    color: '#fff',
    border: 'none',
    borderRadius: 10,
    padding: '14px',
    fontSize: 15,
    fontWeight: 700,
    marginTop: 4,
    letterSpacing: '0.3px',
  },
  footer: {
    textAlign: 'center',
    fontSize: 12,
    color: '#94a3b8',
    marginTop: 32,
  },
};
